import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Avatar from '@/atoms/avatars/Avatar';
import Tooltip from '@/molecules/modals/Tooltip/Tooltip';
import BusinessIcon from '@mui/icons-material/Business';
import ExitIcon from '@mui/icons-material/ExitToApp';
import HelpIcon from '@mui/icons-material/LiveHelp';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import useAccess from '@pkg/access/organisations/useAccess';
import useLogout from '@pkg/auth/useLogout';
import useAuthStore from '@pkg/auth/useStore';
import config from '@/lib/config';
import { core } from '@/lib/theme/tokens/palettes';
import ProfileSubMenu from './ProfileSubMenu';

const ProfileNavigation = () => {
  const router = useRouter();

  const { triggerLogout } = useLogout();
  const me = useAuthStore((state) => state.me);
  const organisation = useAuthStore((state) => state.organisation);
  const showMenu = useAuthStore((state) => state.isFronteggAuthenticated);

  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const access = useAccess();
  const hasAdminAccess = access.model.manage;
  const isRestricted = access.isRestricted;

  const isRoleOnly = me?.is_role_only;
  const showAdminRestricted = hasAdminAccess;

  const [hasIntercom, setIntercom] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setIntercom(Boolean(window?.Intercom));
    }, 500);
  }, []);

  const handleOpenIntercom = () => {
    window?.Intercom?.('show');
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await triggerLogout();
    // fallback to delayed redirect in case we don't receive the websocket event
    setTimeout(() => {
      router.push(config.PATHS.LOGOUT);
    }, 2000);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {showMenu && (
        <>
          <Tooltip title="My profile">
            <Stack
              onClick={handleProfileClick}
              sx={{ cursor: 'pointer' }}
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar
                name={me.__name}
                src={me.avatar}
                sizeProps={{
                  width: '24px',
                  height: '24px',
                  fontSize: '0.75rem',
                }}
              />
            </Stack>
          </Tooltip>
          <ProfileSubMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            showManageProfile={!isRoleOnly}
            me={me}
            organisation={organisation}
          />
        </>
      )}
      {showAdminRestricted && (
        <Tooltip title={organisation?.name}>
          <Link href={config.PATHS.ACCOUNT.index}>
            <BusinessIcon />
          </Link>
        </Tooltip>
      )}
      {hasIntercom && (
        <Tooltip title="Help / FAQ">
          <Link href="#" onClick={handleOpenIntercom}>
            <HelpIcon />
          </Link>
        </Tooltip>
      )}
      <Tooltip title="Sign out" position="bottom" disableFlip>
        <Stack
          onClick={isLoggingOut ? undefined : handleLogout}
          alignItems="center"
          justifyContent="center"
          sx={{
            color: core.white,
            cursor: isLoggingOut ? 'default' : 'pointer',
          }}
        >
          {isLoggingOut ? (
            <CircularProgress size={20} sx={{ color: core.white }} />
          ) : (
            <ExitIcon />
          )}
        </Stack>
      </Tooltip>
    </Stack>
  );
};

export default ProfileNavigation;
